import React, { useState } from "react";
import moment from "moment";
import { useMutation, useQuery } from "@apollo/client";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { ADMIN_GROUP_ORDER, ADMIN_SUBMIT_GROUP_ORDER } from "../../Graphql/group-order.gql";
import { toggleSnackbar } from "../../ReactiveVariables";
import {
  adminStatusLabel,
  budgetLabel,
  formatDelivery,
  missingLabel,
  parseCart,
  serverBudget,
  shareUrl,
} from "../../services/group-order/group-order.utils";

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const when = (d?: string) => (d ? moment(d).format("MMM D, h:mm A") : "—");

export const AdminGroupOrderView = () => {
  const { groupOrderId } = useParams();
  const { data, loading, error, refetch } = useQuery(ADMIN_GROUP_ORDER, {
    variables: { id: groupOrderId },
    fetchPolicy: "network-only",
  });
  const [submitGroupOrder] = useMutation(ADMIN_SUBMIT_GROUP_ORDER);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  if (loading && !data)
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <CircularProgress aria-label="Loading" />
      </div>
    );
  const go = data?.adminGroupOrder;
  if (!go) {
    const notFound = error?.graphQLErrors?.some(
      (e: any) => e.extensions?.code === "GROUP_ORDER_NOT_FOUND",
    );
    return (
      <div className="group-orders-page">
        <p style={{ margin: "16px 0" }}>
          {notFound
            ? "We couldn't find this admin order."
            : "We couldn't load this admin order. Refresh to try again."}
        </p>
      </div>
    );
  }
  const onSubmit = async () => {
    setSubmitting(true);
    try {
      await submitGroupOrder({ variables: { id: go._id } });
      toggleSnackbar({
        status: true,
        message: "Submitted. The rep has been notified.",
        variant: "success",
      });
      await refetch();
    } catch (e: any) {
      const ext = e?.graphQLErrors?.[0]?.extensions;
      toggleSnackbar({
        status: true,
        message:
          ext?.code === "GROUP_ORDER_NOT_READY"
            ? `Can't submit yet: ${(ext.missing || []).map(missingLabel).join(", ")}.`
            : "That didn't go through. Please try again.",
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      setConfirmSubmit(false);
    }
  };
  const cart = parseCart(go);
  const budget = serverBudget(go);
  return (
    <div className="group-orders-page">
      <Link component={RouterLink} to="/group-orders">
        ← Admin Ordering
      </Link>
      <h2 style={{ margin: "16px 0" }}>{go.label || "Admin order"}</h2>
      <p>
        <strong>{adminStatusLabel(go)}</strong>
        {go.status === "Placed" && go.orderId && (
          <>
            {" "}
            ·{" "}
            <Link component={RouterLink} to={`/orders/${go.orderId}`}>
              View order
            </Link>
          </>
        )}
      </p>
      <p>
        Rep: {go.customerName} ({go.customerEmail}) · Caterer: {go.catererName || "Not picked yet"}
        <br />
        {[go.street, go.suit, go.city, go.state, go.zip].filter(Boolean).join(", ") ||
          "Address not set yet"}{" "}
        · {formatDelivery(go)} · {go.headCount || go.maxHeadCount} people (max {go.maxHeadCount})
        <br />
        On-site contact: {go.contactPerson || "—"} {go.contactNo || ""}
        {go.instruction ? ` · ${go.instruction}` : ""}
      </p>
      <p>{budgetLabel(budget, (n: number) => money.format(n))}</p>
      <p style={{ fontSize: 14 }}>
        Created {when(go.createdOn)} · Items last added {when(go.itemsUpdatedAt)} · Submitted{" "}
        {when(go.readyAt)}
        {go.readyByKind === "admin" ? " (by CaterPlace)" : ""} · Placed {when(go.placedAt)}
      </p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        {["Open", "Ready"].includes(go.status) && go.shareToken && (
          <Button
            variant="outlined"
            href={shareUrl(window.location.origin, go.shareToken)}
            target="_blank"
            rel="noopener noreferrer">
            Open office link
          </Button>
        )}
        {go.status === "Open" && (
          <Button variant="contained" onClick={() => setConfirmSubmit(true)}>
            Submit for office
          </Button>
        )}
      </div>
      <Dialog open={confirmSubmit} onClose={submitting ? undefined : () => setConfirmSubmit(false)}>
        <DialogTitle>Submit this order for the office?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {go.customerName || "The rep"} will get an email and text saying CaterPlace submitted
            the order.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={submitting} onClick={() => setConfirmSubmit(false)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={submitting} onClick={onSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell align="right">Price</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cart.map((line: any, i: number) => (
            <TableRow key={line.uuid || i}>
              <TableCell>{line.itemName}</TableCell>
              <TableCell>{line.quantity}</TableCell>
              <TableCell>{line.cartInstruction}</TableCell>
              <TableCell align="right">{money.format(line.cartTotalPrice || 0)}</TableCell>
            </TableRow>
          ))}
          {!cart.length && (
            <TableRow>
              <TableCell colSpan={4}>Nothing has been added to this cart yet.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
